import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Form, Spinner } from "react-bootstrap";
import { useGetLazy, usePost } from "../../api";
import { ICouponAnalyticsSyncState } from "../../interfaces";

const POLL_MS = 3000;
const AUTO_REFRESH_MS = 30 * 60 * 1000;

const secs = (ms: number) => `${Math.round(ms / 1000).toLocaleString()}s`;

/** Where the run's time went, so a slow sync points at SQL or at Mongo. */
const timingText = (s: ICouponAnalyticsSyncState) => {
  const elapsed =
    s.startedAt &&
    (new Date(s.finishedAt ?? Date.now()).getTime() -
      new Date(s.startedAt).getTime()) /
      1000;
  const rate = elapsed && elapsed > 0 ? Math.round(s.runRows / elapsed) : null;
  return `Reading SQL ${secs(s.timing.sqlMs)} · writing Mongo ${secs(
    s.timing.writeMs
  )}${rate != null ? ` · ${rate.toLocaleString()} rows/s` : ""} (reads and writes overlap)`;
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * The Mongo mirror every tab reads. Sync copies new rows from SQL (re-reading
 * the last 48h, since rows can arrive late); a full re-sync re-reads them all.
 */
export const SyncControl: React.FC<{
  onState?: (s: ICouponAnalyticsSyncState) => void;
  /** Called when a sync finishes, so the page can show the new rows. */
  onSynced?: () => void;
  autoRefresh: boolean;
  onAutoRefreshChange: (on: boolean) => void;
}> = ({ onState, onSynced, autoRefresh, onAutoRefreshChange }) => {
  const { execute: load, data } = useGetLazy<ICouponAnalyticsSyncState>();
  const { execute: post, loading: posting } =
    usePost<ICouponAnalyticsSyncState>();
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;
  const [nextAt, setNextAt] = useState<number | null>(null);

  useEffect(() => {
    load("/coupon-performance/sync").catch(() => null);
  }, [load]);

  const busy = data?.status === "pending" || data?.status === "running";
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(
      () => load("/coupon-performance/sync").catch(() => null),
      POLL_MS
    );
    return () => clearInterval(t);
  }, [busy, load]);

  useEffect(() => {
    if (data) onStateRef.current?.(data);
  }, [data]);

  // A new finish time means a sync ended since we last looked, however briefly it ran.
  const lastFinished = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!data) return;
    // First look, even mid-sync: remember the last finish so this run ending still counts.
    if (lastFinished.current === undefined) {
      lastFinished.current = data.finishedAt;
      return;
    }
    if (busy) return;
    if (data.finishedAt !== lastFinished.current) onSyncedRef.current?.();
    lastFinished.current = data.finishedAt;
  }, [data, busy]);

  const request = (full: boolean) =>
    post("/coupon-performance/sync", { full })
      .then(() => load("/coupon-performance/sync"))
      .catch(() => null);
  const requestRef = useRef(request);
  requestRef.current = request;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!autoRefresh) {
      setNextAt(null);
      return;
    }
    setNextAt(Date.now() + AUTO_REFRESH_MS);
    const t = setInterval(() => {
      setNextAt(Date.now() + AUTO_REFRESH_MS);
      // A sync still going after 30 minutes is stuck; show what is there rather than wait on it.
      if (busyRef.current) onSyncedRef.current?.();
      // A failed request still reloads, so the page never sits stale for another 30 minutes.
      else
        requestRef.current(false).then((ok) => {
          if (!ok) onSyncedRef.current?.();
        });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const v = data?.verify;
  const matches = v && !v.error ? v.sqlRows === v.mongoRows : null;

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <small className="text-muted text-end">
        {data?.status === "pending" && "Queued — waiting for the runner"}
        {data?.status === "running" && (
          <span title={timingText(data)}>
            <Spinner size="sm" className="me-1" />
            Syncing{data.full ? " everything" : ""}:{" "}
            {data.runRows.toLocaleString()} rows read · SQL{" "}
            {secs(data.timing.sqlMs)} / Mongo {secs(data.timing.writeMs)}
          </span>
        )}
        {data?.status === "failed" && (
          <span
            className="text-danger d-inline-block text-truncate align-bottom"
            style={{ maxWidth: 420 }}
            title={data.error ?? ""}
          >
            Last sync failed: {data.error ?? "no error recorded"} · Sync resumes
            where it stopped
          </span>
        )}
        {data?.status === "idle" &&
          (data.syncedThrough ? (
            <span
              title={`Last run: ${data.runRows.toLocaleString()} rows. ${timingText(data)}`}
            >
              Synced {fmt(data.finishedAt)} · {data.totalRows.toLocaleString()}{" "}
              rows
            </span>
          ) : (
            "Never synced"
          ))}
      </small>
      {v && !busy && (
        <Badge
          bg={matches ? "success" : "warning"}
          text={matches ? undefined : "dark"}
          title={
            v.error
              ? `The copy finished, but counting rows to compare with SQL failed: ${v.error}`
              : `Rows since ${fmt(v.from)}: ${(v.sqlRows ?? 0).toLocaleString()} in SQL, ${(v.mongoRows ?? 0).toLocaleString()} here. Checked at the end of the last sync.`
          }
        >
          {v.error
            ? "Check failed"
            : matches
              ? "Matches SQL"
              : "Differs from SQL"}
        </Badge>
      )}
      <ButtonGroup size="sm">
        <Button
          variant="outline-primary"
          disabled={busy || posting}
          onClick={() => request(false)}
          title="Copy new rows from the partners-analytics database. Re-reads the last 48 hours, because rows can arrive after newer ones. The results reload when it finishes."
        >
          Sync
        </Button>
        {/* Hidden so nobody re-reads every row by accident; POST /sync { full: true } still works.
        <Button
          variant="outline-secondary"
          disabled={busy || posting}
          onClick={() => request(true)}
          title="Re-read every row from the start. Only needed if the counts stop matching SQL; takes several minutes."
        >
          Full re-sync
        </Button>
        */}
      </ButtonGroup>
      <Form.Check
        type="switch"
        id="coupon-performance-auto-refresh"
        className="small mb-0"
        checked={autoRefresh}
        onChange={(e) => onAutoRefreshChange(e.target.checked)}
        label={nextAt ? `Auto-refresh · next ${clock(nextAt)}` : "Auto-refresh"}
        title="Every 30 minutes while this page is open: sync new rows, then reload the last Search with its window moved up to now."
      />
    </div>
  );
};
